import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/AuthContext";
import { formatApiErrorDetail } from "@/api";
import { Logo } from "@/components/Logo";

export default function Login() {
  const { login, register } = useAuth();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      if (mode === "login") {
        await login(email, password);
      } else {
        await register(email, password, name);
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:block relative border-r border-zinc-200">
        <img
          src="https://images.unsplash.com/photo-1624516268152-1e48624026ed?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400"
          alt="Rolls of coloured fabric"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-zinc-950/55" />
        <div className="absolute bottom-0 p-12">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-white/70">AGARWAL360 · STOCK · SALES · PURCHASE · REPORTS</p>
          <h2 className="font-head mt-3 text-3xl font-extrabold tracking-tighter text-white">
            Your colour-wise stock,
            <br /> ready to export in one tap.
          </h2>
        </div>
      </div>

      <div className="grid-bg flex items-center justify-center p-6">
        <form
          onSubmit={submit}
          data-testid="auth-form"
          className="w-full max-w-sm border border-zinc-200 bg-white p-8"
        >
          <div className="mb-8">
            <Logo size={56} />
          </div>
          <h1 className="font-head text-3xl font-extrabold tracking-tighter">
            {mode === "login" ? "Sign in" : "Create account"}
          </h1>
          <p className="mt-1 text-sm text-zinc-500">
            {mode === "login"
              ? "Sign in with your email and password."
              : "Manage and export your fabric stock."}
          </p>

          <div className="mt-8 space-y-4">
            {mode === "register" && (
              <div>
                <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[var(--mod-primary)]">Name</Label>
                <Input
                  data-testid="auth-name-input"
                  className="mt-2 rounded-sm"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your name"
                />
              </div>
            )}
            <div>
              <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[var(--mod-primary)]">Email</Label>
              <Input
                data-testid="auth-email-input"
                type="email"
                required
                className="mt-2 rounded-sm"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
              />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[var(--mod-primary)]">Password</Label>
              <Input
                data-testid="auth-password-input"
                type="password"
                required
                className="mt-2 rounded-sm"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
              />
            </div>
          </div>

          {error && (
            <p data-testid="auth-error" className="mt-4 border-l-2 border-red-600 bg-red-50 p-2 text-sm text-red-700">
              {error}
            </p>
          )}

          <Button
            data-testid="auth-submit-button"
            type="submit"
            disabled={busy}
            className="mt-6 w-full rounded-sm bg-zinc-950 text-white hover:bg-zinc-800"
          >
            {busy ? "Please wait…" : mode === "login" ? "Sign in" : "Create account"}
          </Button>

          <button
            type="button"
            data-testid="auth-toggle-mode"
            onClick={() => {
              setMode(mode === "login" ? "register" : "login");
              setError("");
            }}
            className="mt-4 w-full text-sm text-zinc-500 underline decoration-zinc-300 hover:text-zinc-950"
          >
            {mode === "login" ? "New here? Create an account" : "Already have an account? Sign in"}
          </button>
        </form>
      </div>
    </div>
  );
}
